import { apiService } from '@/shared/services/api';
import { endpoints } from '@/shared/services/endpoints';
import type { AdAccount, TokenStatus } from '@/shared/types/entities';
import type { ApiResponse } from '@/shared/types/api';

interface BackendAdAccount {
  id: string;
  platform: AdAccount['platform'];
  platformAccountId: string;
  accountName: string;
  isActive: boolean;
  isManaged?: boolean;
  tokenExpiresAt?: string | null;
  lastSyncedAt?: string | null;
  tokenStatus?: TokenStatus;
  missingScopes?: string[];
  loginCustomerId?: string | null;
  createdAt: string;
}

function deriveTokenStatus(account: BackendAdAccount): TokenStatus {
  if (account.tokenStatus) return account.tokenStatus;
  if (!account.tokenExpiresAt) return 'valid';

  const expiresAt = new Date(account.tokenExpiresAt).getTime();
  const now = Date.now();
  const sevenDays = 7 * 24 * 60 * 60 * 1000;

  if (expiresAt <= now) return 'expired';
  if (expiresAt - now <= sevenDays) return 'expiring';
  return 'valid';
}

function mapAccount(account: BackendAdAccount, organizationId: string): AdAccount {
  return {
    id: account.id,
    orgId: organizationId,
    platform: account.platform,
    platformAccountId: account.platformAccountId,
    accountName: account.accountName,
    status: account.isActive ? 'active' : 'disconnected',
    tokenExpiresAt: account.tokenExpiresAt ?? null,
    lastSyncedAt: account.lastSyncedAt ?? account.createdAt,
    tokenStatus: deriveTokenStatus(account),
    missingScopes: account.missingScopes ?? [],
    loginCustomerId: account.loginCustomerId ?? null,
    isManaged: account.isManaged ?? false,
    createdAt: account.createdAt,
    updatedAt: account.createdAt,
  };
}

export async function fetchAccounts(organizationId: string): Promise<AdAccount[]> {
  const { data } = await apiService.get<ApiResponse<BackendAdAccount[]>>(
    endpoints.accounts.list(organizationId),
  );
  return data.data.map((account) => mapAccount(account, organizationId));
}

export async function fetchAccount(organizationId: string, id: string): Promise<AdAccount> {
  const accounts = await fetchAccounts(organizationId);
  const account = accounts.find((item) => item.id === id);
  if (!account) {
    throw new Error('Account not found');
  }
  return account;
}

export async function connectAccount(platform: string, code: string): Promise<AdAccount> {
  const { data } = await apiService.post<ApiResponse<AdAccount>>(
    endpoints.accounts.oauthCallback(platform),
    { code },
  );
  return data.data;
}

export async function disconnectAccount(params: {
  platform: string;
  adAccountId: string;
}): Promise<void> {
  await apiService.delete(endpoints.accounts.disconnect(params.platform, params.adAccountId));
}

export async function syncAccount(id: string): Promise<AdAccount> {
  const { data } = await apiService.post<ApiResponse<AdAccount>>(endpoints.accounts.sync(id));
  return data.data;
}

/** Meta Pixel surfaced by the Marketing API for a given ad account. */
export interface MetaPixel {
  id: string;
  name: string;
  lastFiredAt: string | null;
}

interface MetaPixelListBackendResponse {
  data: MetaPixel[];
}

export async function fetchMetaPixels(adAccountId: string): Promise<MetaPixel[]> {
  const { data } = await apiService.get<ApiResponse<MetaPixelListBackendResponse>>(
    endpoints.accounts.metaPixels(adAccountId),
  );
  return data.data.data;
}

export async function setDefaultMetaPixel(params: {
  adAccountId: string;
  pixelId: string;
}): Promise<{ id: string; defaultPixelId: string }> {
  const { data } = await apiService.put<ApiResponse<{ id: string; defaultPixelId: string }>>(
    endpoints.accounts.defaultPixel(params.adAccountId),
    {
      pixelId: params.pixelId,
    },
  );
  return data.data;
}
